import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { initCapacitor } from './utils/capacitor';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import CrewDashboard from './pages/CrewDashboard';
import Calendar from './pages/Calendar';
import Projects from './pages/Projects';
import Leads from './pages/Leads';
import CrewMembers from './pages/CrewMembers';
import Finance from './pages/Finance';
import Gear from './pages/Gear';
import Payments from './pages/Payments';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import WorkflowWrapper from './pages/WorkflowWrapper';
import AlbumProcessing from './pages/AlbumProcessing';
import DayBoard from './pages/DayBoard';
import ConfirmGear from './pages/ConfirmGear';
import PublicShotList from './pages/PublicShotList';
import Privacy from './pages/Privacy';
import Inquiry from './pages/Inquiry';
import LoadingScreen from './components/LoadingScreen';
import './App.css';

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  return user ? children : <Navigate to="/login" />;
};

const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;
  if (!isAdmin) return <Navigate to="/" />;
  return children;
};

const SmartDashboard = () => {
  const { isAdmin } = useAuth();
  return isAdmin ? <Dashboard /> : <CrewDashboard />;
};

// Mounts inside Router so we have access to useNavigate; boots Capacitor plugins on native shells.
const CapacitorBridge = () => {
  const navigate = useNavigate();
  useEffect(() => {
    initCapacitor(navigate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

const AppRoutes = () => {
  return (
    <Router>
      <CapacitorBridge />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/confirm-gear" element={<ConfirmGear />} />
        <Route path="/shotlist/:token" element={<PublicShotList />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/privacy.html" element={<Privacy />} />
        <Route path="/inquiry" element={<Inquiry />} />
        <Route path="/" element={<PrivateRoute><SmartDashboard /></PrivateRoute>} />
        <Route path="/calendar" element={<PrivateRoute><Calendar /></PrivateRoute>} />
        <Route path="/leads" element={<AdminRoute><Leads /></AdminRoute>} />
        <Route path="/schedule" element={<AdminRoute><DayBoard /></AdminRoute>} />
        <Route path="/projects" element={<AdminRoute><Projects /></AdminRoute>} />
        <Route path="/crew" element={<AdminRoute><CrewMembers /></AdminRoute>} />
        <Route path="/gear" element={<AdminRoute><Gear /></AdminRoute>} />
        <Route path="/payments" element={<AdminRoute><Payments /></AdminRoute>} />
        <Route path="/analytics" element={<AdminRoute><Analytics /></AdminRoute>} />
        <Route path="/finance" element={<AdminRoute><Finance /></AdminRoute>} />
        <Route path="/settings" element={<AdminRoute><Settings /></AdminRoute>} />
        <Route path="/workflow" element={<AdminRoute><WorkflowWrapper /></AdminRoute>} />
      </Routes>
    </Router>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
